import type { TemplateCategorySpec } from './types';

export const PERSONAL_INFORMATION: TemplateCategorySpec = {
  slug: "personal-information",
  name: "Personal Information",
  categoryType: "personal_info",
  icon: "User",
  sortOrder: 7, // s111 Build 6 — renumbered 6→7 (Protect Your Access inserted at 1)
  isActive: true,
  isSystem: false,
  sections: {
      "personal-information-government-ids": {
        slug: "personal-information-government-ids",
        priority: 3,
        name: "Government IDs",
        icon: "IdCard",
        description: null,
        // s73: expandable (Path X). Gov IDs' 4 default rows are `_locked` (virtual,
        // never stored). The s72 revert blamed the renderer, but the real cause was
        // the PARENT: `tableSectionRows` Pass-2 virtual-row synthesis was gated to
        // `layoutType === 'table'`, so a flip stopped producing the `__virt__` rows.
        // ExpandableSectionLayout itself has handled virtual + locked rows since s68
        // (489814e). Fix = extend that gate to allow 'expandable' (GenericCategoryPage
        // ~line 1633); no renderer change.
        layoutType: "expandable",
        sortOrder: 2,
        isActive: true,
        allowDocuments: true,
        defaultRows: [
    {
      "notes": "Keep the physical card stored securely — not in your wallet.",
      "_locked": "true",
      "id_type": "Social Insurance Number (SIN)",
      "_govForms": "ontario-statement-of-death,cpp-death-benefit,cra-t1-final,form-74a,ontario-eir,serviceontario-cancellations"
    },
    {
      "notes": "Note renewal date and provincial authority.",
      "_locked": "true",
      "id_type": "Driver's License",
      "_govForms": "ontario-statement-of-death,serviceontario-cancellations"
    },
    {
      "notes": "Note expiry date — passports need renewal well in advance of travel.",
      "_locked": "true",
      "id_type": "Passport",
      "_govForms": "serviceontario-cancellations"
    },
    {
      "notes": "Note expiry date and provincial health authority.",
      "_locked": "true",
      "id_type": "Health Card (OHIP)",
      "_govForms": "serviceontario-cancellations"
    }
  ],
        aiSummary: null,
        wizardStage: 8,
        wizardDescription: null,
        wizardAllowAddAnother: false,
        wizardAddAnotherLabel: null,
        consumerTag: "both",
        wizardInclusionRule: "always",
        fields: {
        "person_name": {
          fieldKey: "person_name",
          name: "Whose ID?",
          // 🔴 REVERTED to `text` at s205 (D-205.9). PERSON-FIELDS-1 flipped this to `contact`
          // with the other 27; that flip was wrong for THIS field alone, because
          // `syncGovIdNamesFromIdentity` (server/govid-name-sync.ts, s110) writes the planner's
          // legal name into it as a PLAIN STRING on every name edit. A reference-typed field with
          // a plain-string writer produced 39 of production's 114 malformed contact values, and
          // it regenerates for every new signup — so the type, not the writer, is what moves here.
          // Repointing the s110 subsystem is a separate, unruled decision and was NOT made.
          fieldType: "text",
          placeholder: "e.g. your name, child's name",
          icon: "User",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 2,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 0,
          columnWidth: 160,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "id_type": {
          fieldKey: "id_type",
          identityOrdinal: 1, // s199 FIELD-ROLE-IDENTITY-1
          labelCurated: true, // s199 Phase 3 — the label is a curated choice
          specifyOnOther: true, // s155 OTHER-SPECIFY-1 (D-155.6)
          name: "ID Type",
          fieldType: "select",
          placeholder: null,
          icon: "FileText",
          // s223 (D-222.10 part 5): "Accessible parking permit" and "Status card" APPENDED — the two the
          // interview's Government ID screen offers that had no stored type. 🔴 A SECOND, different id_type list
          // of eight choices sits in children-and-dependents.ts; it is NOT touched and the two are NOT merged —
          // one fact in two lists is registered work, not this build's.
          options: { "choices": ["Social Insurance Number (SIN)", "Driver's License", "Passport", "Health Card (OHIP)", "Permanent Resident Card", "Citizenship Certificate", "Military ID", "Nexus / Global Entry", "Birth Certificate", "Other", "Accessible parking permit", "Status card"] },
          isRequired: true,
          isCritical: false,
          wizardStage: 2,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 1,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "id_number": {
          fieldKey: "id_number",
          declineEligible: true, // FIELD-AUDIT-1 R4 (D-154.4): Cat-A credential — presence-metadata only
          name: "Number",
          fieldType: "password",
          placeholder: "ID number (kept in your secure file)",
          icon: "FileText",
          options: null,
          isRequired: true, // s79 star pass — readiness value field (gov IDs). Category A — presence via catAFieldPresent
          isCritical: false,
          wizardStage: 2,
          isEncrypted: true,
          isCategoryA: true,
          sortOrder: 2,
          columnWidth: 160,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: ["ontario-statement-of-death", "cpp-death-benefit", "cra-t1-final", "form-74a", "ontario-eir", "serviceontario-cancellations"],
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "expiry": {
          fieldKey: "expiry",
          name: "Expiry Date",
          fieldType: "date",
          placeholder: null,
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 2,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 3,
          columnWidth: 120,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "location": {
          fieldKey: "location",
          aggregateRole: "location",
          name: "Location",
          fieldType: "text",
          placeholder: "Where the physical document is kept",
          icon: "FileText",
          options: null,
          // s223 (D-222.10 part 5): OPTIONAL, matching the board's "Where is it kept? — optional". The decline
          // beside it stays: a planner who does not hold the document still says so rather than leaving a blank.
          isRequired: false,
          isCritical: false,
          wizardStage: 2,
          isEncrypted: false,
          isCategoryA: false,
          declineEligible: true, // s155 D-155.2: planner may not hold the physical doc — escape parity with the id_number companion
          sortOrder: 4,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "notes": {
          fieldKey: "notes",
          name: "Notes",
          fieldType: "notes",
          placeholder: null,
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 2,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 5,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        }
        },
      },
      "personal-information-health-medical": {
        slug: "personal-information-health-medical",
        priority: 1,
        name: "Your Health & Medical",
        icon: "Heart",
        description: null,
        layoutType: "expandable",
        sortOrder: 4,
        // FIELD-AUDIT-1 (D-154.5): RETIRED — the one executor-relevant fact (organ_donor_status)
        // relocated to About You as a starred field; the rest (blood type / allergies / medications /
        // conditions / directive) folded away. isActive:false + defaultRows:null so the seeder +
        // validator skip it (empty-defaultRows guard). personal-information-health-insurance STAYS.
        isActive: false,
        allowDocuments: true,
        defaultRows: null,
        aiSummary: null,
        wizardStage: 2,
        wizardDescription: null,
        wizardAllowAddAnother: false,
        wizardAddAnotherLabel: null,
        consumerTag: "both",
        wizardInclusionRule: "always",
        fields: {
        "category": {
          fieldKey: "category",
          name: "Category",
          fieldType: "select",
          placeholder: null,
          icon: "FileText",
          options: { "choices": ["Blood Type", "Known Allergies", "Current Medications", "Medical Conditions", "Organ Donor Status", "Medical Directive", "Other"] },
          isRequired: false,
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 0,
          columnWidth: 200,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "details": {
          fieldKey: "details",
          name: "Details",
          fieldType: "text",
          placeholder: "Enter details",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 1,
          columnWidth: 400,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "notes": {
          fieldKey: "notes",
          name: "Notes",
          fieldType: "notes",
          placeholder: null,
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 2,
          columnWidth: 200,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        // s95 (Option 2): section-HEADER field. Rendered above the record list (not
        // per-row) via section-layout-config `headerField`; stored on the dedicated
        // `__header__personal-information-health-medical` row. It is the section's
        // completion criterion (computeSectionProgress header branch). Do NOT add it
        // to basicFieldKeys/groups — it must not double-render per record.
        "organ_donor_status": {
          fieldKey: "organ_donor_status",
          name: "Organ Donor Status",
          fieldType: "select",
          placeholder: null,
          icon: "Heart",
          options: { "choices": ["Registered donor", "Not a donor", "Undecided"] },
          isRequired: true,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 3,
          columnWidth: null,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: "Whether you are a registered organ/tissue donor — shown as the section header.",
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        }
        },
      },
      "personal-information-health-insurance": {
        slug: "personal-information-health-insurance",
        priority: 3,
        name: "Health Insurance",
        icon: "Shield",
        description: null,
        layoutType: "expandable",
        sortOrder: 7,
        isActive: true,
        allowDocuments: false,
        // s106 (MIG-1 / DRA-1): two starter rows. Appended to a previously-empty section,
        // so seeded as __seeded__personal-information-health-insurance__0/__1 — curated
        // (shaded) on plan_type via TOP_ZONE_SCAFFOLDS + TOP_ZONE_CURATION_FIELD override
        // (provider, the first field, stays blank for the planner to fill). Requires a
        // template_version bump + heal (heal-first).
        // 🔴 s177 (D-177.4, COMPLETION-REALITY-1): every row below is DEACTIVATED IN PLACE.
        // These were the product's guesses; the mandatory onboarding interview asks instead and
        // creates only the rows the planner actually has. Kept in the array (NEVER spliced,
        // D-106.1) so surviving __seeded__/__builtin__ indices and the _govForms index basis hold.
        // D-177.9c: rows retire, VOCABULARY does not — every option list below is untouched.
        defaultRows: [
          {
      "_inactive": "true", "plan_type": "Extended Health" },
          {
      "_inactive": "true", "plan_type": "Dental" }
        ],
        aiSummary: null,
        wizardStage: 8,
        wizardDescription: null,
        wizardAllowAddAnother: true,
        wizardAddAnotherLabel: null,
        consumerTag: "both",
        wizardInclusionRule: "manual_only",
        fields: {
        "provider": {
          fieldKey: "provider",
          identityOrdinal: 1, // s199 FIELD-ROLE-IDENTITY-1
          name: "Provider",
          fieldType: "text",
          placeholder: "e.g. Sun Life, Manulife, Blue Cross",
          icon: "FileText",
          options: null,
          // 🔴 s177 (A2, D-177.10): the section had ZERO effective starred fields, so under the
          // interim bar a blank row was instantly "fully filled" and the section went green on a
          // row containing nothing. Starring the insurer name means a row must at least say WHAT
          // IT IS. Fixed in the spec rather than special-cased in the math (D-177.10).
          isRequired: true,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 0,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "policy_number": {
          fieldKey: "policy_number",
          name: "Policy Number",
          fieldType: "text",
          placeholder: "Policy or group number",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 1,
          columnWidth: 160,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "plan_type": {
          fieldKey: "plan_type",
          specifyOnOther: true, // s155 OTHER-SPECIFY-1 (D-155.6)
          name: "Plan Type",
          fieldType: "select",
          placeholder: null,
          icon: "FileText",
          options: { "choices": ["Extended Health", "Dental", "Employer Group", "Individual", "Government", "OHIP Supplement", "Travel", "Other"] },
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 2,
          columnWidth: 140,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "coverage": {
          fieldKey: "coverage",
          name: "Coverage",
          fieldType: "text",
          placeholder: "e.g. Dental, Vision, Extended Health",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 3,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "contact_phone": {
          fieldKey: "contact_phone",
          name: "Contact / Phone",
          fieldType: "phone",
          placeholder: null,
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 4,
          columnWidth: 150,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "username": {
          fieldKey: "username",
          name: "Username",
          fieldType: "text",
          placeholder: "Username or email",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 10,
          columnWidth: null,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "account_email": {
          fieldKey: "account_email",
          name: "Account Email",
          fieldType: "email",
          placeholder: "Recovery / notification email",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 11,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "password": {
          fieldKey: "password",
          name: "Password",
          fieldType: "password",
          placeholder: "Password",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: true,
          isCategoryA: true,
          sortOrder: 11,
          columnWidth: null,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: false,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "notes": {
          fieldKey: "notes",
          name: "Notes",
          fieldType: "notes",
          placeholder: null,
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 20,
          columnWidth: 200,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        }
        },
      },
      "personal-information-career": {
        slug: "personal-information-career",
        priority: 3,
        name: "Employment History",
        icon: "Briefcase",
        description: null,
        layoutType: "expandable",
        sortOrder: 9,
        isActive: true,
        allowDocuments: false,
        // s95→FIELD-AUDIT-1 (D-154.5): one starter row, now fully BLANK. employment_status no
        // longer pre-answers "Active Employee" (a scaffold value that read as a real answer);
        // the planner picks it. Both cells empty → the row is a pure scaffold.
        // 🔴 s177 (D-177.4, COMPLETION-REALITY-1): every row below is DEACTIVATED IN PLACE.
        // These were the product's guesses; the mandatory onboarding interview asks instead and
        // creates only the rows the planner actually has. Kept in the array (NEVER spliced,
        // D-106.1) so surviving __seeded__/__builtin__ indices and the _govForms index basis hold.
        // D-177.9c: rows retire, VOCABULARY does not — every option list below is untouched.
        defaultRows: [
    {
      "_inactive": "true",
      // s142 (FIELDFLICKER-1 Fix 4): the starter row ships BLANK — the old "Current employer"
      // value was a scaffold that read as real data a tester had to delete. The row still floats
      // to the expanded section's TOP zone, now curated by seeded-row IDENTITY
      // (TOP_ZONE_SEEDED_STARTER_SECTIONS) rather than by this value.
      "employer": "",
      "employment_status": ""
    }
  ],
        aiSummary: null,
        wizardStage: 4,
        wizardDescription: null,
        wizardAllowAddAnother: true,
        wizardAddAnotherLabel: null,
        consumerTag: "both",
        wizardInclusionRule: "manual_only",
        fields: {
        "employer": {
          fieldKey: "employer",
          identityOrdinal: 1, // s199 FIELD-ROLE-IDENTITY-1
          name: "Employer",
          fieldType: "text",
          placeholder: "Company or organization",
          icon: "FileText",
          options: null,
          isRequired: true, // s95 completion-audit B3: give Employment History a completion criterion
          declineEligible: true, // s179 TWO-REASON-DECLINE-1 (D-178.15): letter-consumed + starred, so the planner could previously neither fill it nor exit it honestly. Three honest exits: dont_have / wont_store / giving_another_way.
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 0,
          columnWidth: 200,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "job_title": {
          fieldKey: "job_title",
          name: "Job Title",
          fieldType: "text",
          placeholder: "e.g. Senior Engineer",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 1,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "start_year": {
          fieldKey: "start_year",
          name: "Start Year",
          fieldType: "text",
          placeholder: "e.g. 2010",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 2,
          columnWidth: 100,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "end_year": {
          fieldKey: "end_year",
          name: "End Year",
          fieldType: "text",
          placeholder: "e.g. 2018 or Present",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 3,
          columnWidth: 100,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "notes": {
          fieldKey: "notes",
          name: "Notes",
          fieldType: "notes",
          placeholder: null,
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 4,
          columnWidth: 200,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "employment_status": {
          fieldKey: "employment_status",
          name: "Status",
          fieldType: "select",
          placeholder: null,
          icon: "FileText",
          options: { "choices": ["Active Employee", "Retired / Former"] },
          isRequired: true, // s95 completion-audit B3
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 5,
          columnWidth: 150,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "employer_address": {
          fieldKey: "employer_address",
          name: "Employer Address",
          fieldType: "address",
          placeholder: "Employer address",
          icon: "FileText",
          options: null,
          isRequired: true, // s155 D-155.7: STARRED — consumed by the employer notification letters
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          declineEligible: true, // s155 D-155.7: honest exit if the address isn't known
          sortOrder: 6,
          columnWidth: 200,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "hr_contact_name": {
          fieldKey: "hr_contact_name",
          name: "HR / Contact",
          fieldType: "contact",
          placeholder: "HR department or contact person",
          icon: "FileText",
          options: null,
          isRequired: true, // s155 D-155.7: STARRED — consumed by the employer notification letters
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          declineEligible: true, // s155 D-155.7: honest exit if no HR contact is known
          sortOrder: 7,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "hr_contact_phone": {
          fieldKey: "hr_contact_phone",
          name: "Phone",
          fieldType: "phone",
          placeholder: "Phone number",
          icon: "FileText",
          options: null,
          isRequired: false, // s101: relaxed for shaded mode (HR contact optional; was s95 B3)
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 8,
          columnWidth: 150,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "hr_contact_email": {
          fieldKey: "hr_contact_email",
          name: "Email",
          fieldType: "email",
          placeholder: "Email address",
          icon: "FileText",
          options: null,
          isRequired: false, // s101: relaxed for shaded mode (HR contact optional; was s95 B3)
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 9,
          columnWidth: 170,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "employer_phone": {
          fieldKey: "employer_phone",
          name: "Employer Phone",
          fieldType: "phone",
          placeholder: "Main number",
          icon: "FileText",
          options: null,
          isRequired: false, // s237 chapter three PR 2 (the Work screen, D-236.61): appended, optional — the interview binds it; classic completion unchanged.
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 10,
          columnWidth: 160,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "employer_email": {
          fieldKey: "employer_email",
          name: "Employer Email or Website",
          fieldType: "email",
          placeholder: "info@company.ca or company.ca",
          icon: "FileText",
          options: null,
          isRequired: false, // s237 chapter three PR 2 (the Work screen, D-236.61): appended, optional — the interview binds it; classic completion unchanged.
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 11,
          columnWidth: 160,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "employee_number": {
          fieldKey: "employee_number",
          name: "Employee Number",
          fieldType: "text",
          placeholder: "If you know it",
          icon: "FileText",
          options: null,
          isRequired: false, // s237 chapter three PR 2 (the Work screen, D-236.61): appended, optional — the interview binds it; classic completion unchanged.
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 12,
          columnWidth: 160,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "career_kind": {
          fieldKey: "career_kind",
          name: "Kind",
          fieldType: "select",
          placeholder: null,
          icon: "FileText",
          options: { "choices": ["Job", "Other work", "Past employer"] },
          isRequired: false, // s237 chapter three PR 2 (the Work screen, D-236.61): appended, optional — the interview binds it; classic completion unchanged.
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 13,
          columnWidth: 160,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "hr_contact_address": {
          fieldKey: "hr_contact_address",
          name: "HR / Contact Address",
          fieldType: "address",
          placeholder: null,
          icon: "FileText",
          options: null,
          isRequired: false, // s237 chapter three PR 2 (the Work screen, D-236.61): appended, optional — the interview binds it; classic completion unchanged.
          isCritical: false,
          wizardStage: 4,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 14,
          columnWidth: 160,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        }
        },
      },
      "family-friends-and-pets-important-contacts": {
        slug: "family-friends-and-pets-important-contacts",
        priority: 3,
        name: "Important Contacts",
        icon: "BookUser",
        description: null,
        layoutType: "expandable",
        sortOrder: 11,
        isActive: true,
        allowDocuments: false,
        // 🔴 s177 (D-177.4, COMPLETION-REALITY-1): every row below is DEACTIVATED IN PLACE.
        // These were the product's guesses; the mandatory onboarding interview asks instead and
        // creates only the rows the planner actually has. Kept in the array (NEVER spliced,
        // D-106.1) so surviving __seeded__/__builtin__ indices and the _govForms index basis hold.
        // D-177.9c: rows retire, VOCABULARY does not — every option list below is untouched.
        defaultRows: [
    {
      "_inactive": "true",
      "name": "Emergency Contact 1",
      "notes": "First person to call in an emergency."
    },
    {
      "_inactive": "true",
      "name": "Emergency Contact 2",
      "notes": "Second emergency contact."
    },
    {
      "_inactive": "true",
      "name": "Close Friend",
      "notes": "Close friend who should be notified."
    },
    {
      "_inactive": "true",
      "name": "Neighbour",
      "notes": "Nearby neighbour with access or a spare key."
    },
    {
      // FIELD-AUDIT-1 (D-154.5): dropped as a starter — retired in place (_inactive, D-106.1;
      // last row so no reindex). Landlord details live in Things I Rent (financial-and-insurance).
      "_inactive": "true",
      "name": "Landlord / Property Manager",
      "notes": "If renting — note notice requirements and security deposit details."
    }
  ],
        aiSummary: null,
        wizardStage: 7,
        wizardDescription: null,
        wizardAllowAddAnother: true,
        wizardAddAnotherLabel: "contact",
        consumerTag: "both",
        wizardInclusionRule: "always",
        fields: {
        "name": {
          fieldKey: "name",
          identityOrdinal: 1, // s199 FIELD-ROLE-IDENTITY-1
          name: "Full Name",
          fieldType: "text",
          placeholder: "Full name",
          icon: "FileText",
          options: null,
          isRequired: true,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 0,
          columnWidth: 170,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "relationship": {
          fieldKey: "relationship",
          specifyOnOther: true, // s155 OTHER-SPECIFY-1 (D-155.6)
          name: "Relationship",
          fieldType: "select",
          placeholder: "e.g. Sister, Neighbour",
          icon: "FileText",
          options: { "choices": ["Spouse / Partner", "Child", "Grandchild", "Parent", "Sibling", "Other Family", "Friend", "Neighbour", "Other"] },
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 1,
          columnWidth: 130,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "phone": {
          fieldKey: "phone",
          name: "Phone",
          fieldType: "phone",
          placeholder: "Phone number",
          icon: "FileText",
          options: null,
          isRequired: true, // s155 D-155.8: notify-family-friends — how to reach the contact
          declineEligible: true, // s155 D-155.8: 3rd-party contact
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 2,
          columnWidth: 130,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "email": {
          fieldKey: "email",
          name: "Email",
          fieldType: "email",
          placeholder: "Email address",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 3,
          columnWidth: 170,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "address": {
          fieldKey: "address",
          name: "Address",
          fieldType: "address",
          placeholder: "Mailing address",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 41,
          columnWidth: 200,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "notes": {
          fieldKey: "notes",
          name: "Notes",
          fieldType: "notes",
          placeholder: null,
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 4,
          columnWidth: 140,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        }
        },
      },
      "family-pets": {
        // s171 Block 3b item 4: retitled in place to match the widened About You question. The
        // SLUG stays `family-pets` — retitling is a display change; re-slugging would orphan every
        // existing row, every constant reference, and the gate's signal-section mapping.
        // s202 (D-202.5): retitled again, "Pets & Livestock" → "Pets". Display only, same reasoning
        // — the slug does not move. The About You question stays broad on purpose (about-you.ts),
        // so a livestock-only planner still reaches this section.
        slug: "family-pets",
        priority: 3,
        name: "Pets",
        icon: "PawPrint",
        description: null,
        layoutType: "expandable",
        sortOrder: 12,
        isActive: true,
        allowDocuments: true,
        // 🔴 s177 (D-177.4, COMPLETION-REALITY-1): every row below is DEACTIVATED IN PLACE.
        // These were the product's guesses; the mandatory onboarding interview asks instead and
        // creates only the rows the planner actually has. Kept in the array (NEVER spliced,
        // D-106.1) so surviving __seeded__/__builtin__ indices and the _govForms index basis hold.
        // D-177.9c: rows retire, VOCABULARY does not — every option list below is untouched.
        defaultRows: [
    {
      "_inactive": "true",
      "pet_name": "Your pet",
      "notes": "Note any special dietary needs, medications, routine, and the location of vaccination records."
    }
  ],
        aiSummary: null,
        wizardStage: 2,
        wizardDescription: null,
        wizardAllowAddAnother: true,
        wizardAddAnotherLabel: "pet",
        consumerTag: "both",
        wizardInclusionRule: "conditional",
        fields: {
        "pet_name": {
          fieldKey: "pet_name",
          identityOrdinal: 1, // s199 FIELD-ROLE-IDENTITY-1
          // s171 Block 3b item 4: mirrors the About You capture label. A pet has a name; a herd
          // has a description. One field carries both — no new field, no second section.
          name: "Name or description",
          fieldType: "text",
          placeholder: 'Biscuit, or "40 beef cattle"',
          icon: "FileText",
          options: null,
          isRequired: true,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 0,
          columnWidth: 130,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "pet_type": {
          fieldKey: "pet_type",
          specifyOnOther: true, // s155 OTHER-SPECIFY-1 (D-155.6)
          name: "Type",
          fieldType: "select",
          placeholder: null,
          icon: "FileText",
          // s171 Block 3b item 4: livestock options APPENDED (never reordered) so existing stored
          // values keep resolving and the familiar pet types stay where a pet owner expects them.
          // Kept "Horse" rather than adding a duplicate "Horses" — it already serves both readings.
          // Mirrors the About You capture list; the two must not drift.
          //
          // 🔴 s224 (D-223.36 ruling 3, Reade: "agree") — THE BOARD OWNS THIS LIST. `Main.dc.html`'s
          // `pets` step offers seven: Dog · Cat · Bird · Horse · Cattle or other livestock · Small
          // animal or reptile · Other. So six stored choices COME OUT (Fish, Rabbit, Reptile,
          // Poultry, Sheep / Goats, Other livestock) and one is APPENDED in the list's own noun
          // style — the s223 beneficiaries precedent, where the four the board offered with no
          // stored equivalent were appended rather than the board reworded. The six survivors keep
          // their original relative order (appended, never reordered). Board wording → stored value
          // is declared in `shared/interview/board-map.ts` OPTION_VALUE.pets; `check:board-parity`
          // fails on any board option with no stored value now that `pets` is a BUILT screen.
          //
          // 🔴 This is a shrink, so D-177.9c ("rows retire, VOCABULARY does not") is DEPARTED FROM,
          // knowingly and by ruling: the rows holding a removed value are fixtures (D-205.2,
          // D-203.17) and are deleted in this same lane, so no stored value is left unresolvable.
          options: { "choices": ["Dog", "Cat", "Bird", "Horse", "Other", "Cattle", "Small animal or reptile"] },
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 1,
          columnWidth: 120,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "breed": {
          fieldKey: "breed",
          name: "Breed",
          fieldType: "text",
          placeholder: "Breed or description",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 2,
          columnWidth: 130,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "vet": {
          fieldKey: "vet",
          name: "Vet / Clinic",
          fieldType: "text",
          placeholder: "Veterinarian or clinic name",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 3,
          columnWidth: 170,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          // 🔴 s224 RETIRED (D-196.1 act a; D-223.36 ruling 2, Reade: "agree"). The board models the
          // vet as its OWN ROW in the second table, while this was a column on EVERY pet row —
          // three pets meant the vet written three times, which is four copies of one organisation
          // and exactly what D-202.1 ("one contact record, referenced everywhere") exists to stop.
          // Replaced by the vet row's `vet_practice` / `vet_contact` + card answers, below.
          // Act b (readers repointed) and act c (stored values deleted) both discharged in this lane.
          // NEVER deleted from this object — a spliced field orphans stored entries (D-106.1).
          isActive: false,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "vet_phone": {
          fieldKey: "vet_phone",
          name: "Vet Phone",
          fieldType: "phone",
          placeholder: "Phone number",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 4,
          columnWidth: 130,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          // 🔴 s224 RETIRED (D-196.1 act a; D-223.36 ruling 2, Reade: "agree"). The board models the
          // vet as its OWN ROW in the second table, while this was a column on EVERY pet row —
          // three pets meant the vet written three times, which is four copies of one organisation
          // and exactly what D-202.1 ("one contact record, referenced everywhere") exists to stop.
          // Replaced by the vet row's `vet_practice` / `vet_contact` + card answers, below.
          // Act b (readers repointed) and act c (stored values deleted) both discharged in this lane.
          // NEVER deleted from this object — a spliced field orphans stored entries (D-106.1).
          isActive: false,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "vet_email": {
          fieldKey: "vet_email",
          name: "Vet Email",
          fieldType: "email",
          placeholder: "Clinic email",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 41,
          columnWidth: 170,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          // 🔴 s224 RETIRED (D-196.1 act a; D-223.36 ruling 2, Reade: "agree"). The board models the
          // vet as its OWN ROW in the second table, while this was a column on EVERY pet row —
          // three pets meant the vet written three times, which is four copies of one organisation
          // and exactly what D-202.1 ("one contact record, referenced everywhere") exists to stop.
          // Replaced by the vet row's `vet_practice` / `vet_contact` + card answers, below.
          // Act b (readers repointed) and act c (stored values deleted) both discharged in this lane.
          // NEVER deleted from this object — a spliced field orphans stored entries (D-106.1).
          isActive: false,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "vet_address": {
          fieldKey: "vet_address",
          name: "Vet Address",
          fieldType: "address",
          placeholder: "Clinic address",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 42,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          // 🔴 s224 RETIRED (D-196.1 act a; D-223.36 ruling 2, Reade: "agree"). The board models the
          // vet as its OWN ROW in the second table, while this was a column on EVERY pet row —
          // three pets meant the vet written three times, which is four copies of one organisation
          // and exactly what D-202.1 ("one contact record, referenced everywhere") exists to stop.
          // Replaced by the vet row's `vet_practice` / `vet_contact` + card answers, below.
          // Act b (readers repointed) and act c (stored values deleted) both discharged in this lane.
          // NEVER deleted from this object — a spliced field orphans stored entries (D-106.1).
          isActive: false,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "insurance": {
          fieldKey: "insurance",
          name: "Pet Insurance",
          fieldType: "text",
          placeholder: "Provider & policy #",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 5,
          columnWidth: 150,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "guardian": {
          fieldKey: "guardian",
          name: "Guardian (Full Name)",
          fieldType: "contact",
          placeholder: "Who will care for them",
          icon: "FileText",
          options: null,
          isRequired: true, // FIELD-AUDIT-1 (D-154.5): starred (pet guardian)
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          declineEligible: true, // s155 D-155.2: a pet guardian may not be chosen yet — escape parity with child-guardian fields
          sortOrder: 6,
          columnWidth: 160,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        // 🔴 s224 (D-223.36 ruling 1) — THE KIND COLUMN, the `kids` precedent exactly (D-221.10).
        // The board's `pets` step declares two tables: pets, and `second: { tiles: ['vet'] }` for
        // the vet. Both are ROWS of this one section; this column says which, and the screen splits
        // the drawn rows by it through the shared `TileTables`. A row typed on the planner page
        // before this field existed carries no kind and reads as a Pet, which is what it is.
        "row_kind": {
          fieldKey: "row_kind",
          name: "What",
          fieldType: "select",
          placeholder: null,
          icon: "FileText",
          options: { "choices": ["Pet", "Vet"] },
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 8,
          columnWidth: 110,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        // s224 CONTACT-CARD-ONE-1 — the board's `who` cell is the ONE standard contact card, so the
        // guardian keeps the same three answers every other card does (the `kids` guardian slots'
        // shape: primary_guardian + _phone / _email / _address). `guardian` itself is untouched —
        // still `contact`-typed, still starred, still decline-eligible (D-155.2).
        "guardian_phone": {
          fieldKey: "guardian_phone",
          name: "Guardian Phone",
          fieldType: "phone",
          placeholder: "Best number",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 9,
          columnWidth: 130,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "guardian_email": {
          fieldKey: "guardian_email",
          name: "Guardian Email",
          fieldType: "email",
          placeholder: "name@example.com",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 10,
          columnWidth: 170,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "guardian_address": {
          fieldKey: "guardian_address",
          name: "Guardian Address",
          fieldType: "address",
          placeholder: "Their address",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 11,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        // 🔴 s224 (D-223.36 ruling 2) — THE VET ROW's own fields, replacing the four retired columns.
        // The board's second table asks two things: "The practice" (the exact name) and "Who to
        // call" (the practice, or a person there) — the latter through the standard contact card
        // with `org: true`, so the vet lands on the executor's contact list in one shape (s211).
        "vet_practice": {
          fieldKey: "vet_practice",
          name: "The practice",
          fieldType: "text",
          placeholder: "e.g. Bloor West Animal Hospital",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 12,
          columnWidth: 220,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "vet_contact": {
          fieldKey: "vet_contact",
          name: "Who to call",
          fieldType: "contact",
          placeholder: "The practice, or a person there",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 13,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "vet_contact_phone": {
          fieldKey: "vet_contact_phone",
          name: "Who to call — phone",
          fieldType: "phone",
          placeholder: "Main number",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 14,
          columnWidth: 130,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "vet_contact_email": {
          fieldKey: "vet_contact_email",
          name: "Who to call — email",
          fieldType: "email",
          placeholder: "info@practice.ca",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 15,
          columnWidth: 170,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "vet_contact_address": {
          fieldKey: "vet_contact_address",
          name: "Who to call — address",
          fieldType: "address",
          placeholder: "The practice address",
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 16,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        // s236 (D-236.25(3), D-236.40(1); rule 150): the vet's company card — a person to ask for at the practice, by reference.
        "vet_ask_for": {
          fieldKey: "vet_ask_for",
          name: "The practice: a person to ask for",
          fieldType: "contact",
          placeholder: null,
          icon: "Users",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 43,
          columnWidth: null,
          layoutWidth: "half",
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        // s236 (D-236.25(3), D-236.40(1); rule 150): the vet's company card — a person to ask for at the practice, by reference.
        "vet_ask_for_phone": {
          fieldKey: "vet_ask_for_phone",
          name: "The practice: the person to ask for: phone",
          fieldType: "phone",
          placeholder: null,
          icon: "Phone",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 44,
          columnWidth: null,
          layoutWidth: "half",
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        // s236 (D-236.25(3), D-236.40(1); rule 150): the vet's company card — a person to ask for at the practice, by reference.
        "vet_ask_for_email": {
          fieldKey: "vet_ask_for_email",
          name: "The practice: the person to ask for: email or website",
          fieldType: "email",
          placeholder: null,
          icon: "Mail",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 45,
          columnWidth: null,
          layoutWidth: "half",
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        // s236 (D-236.25(3), D-236.40(1); rule 150): the vet's company card — a person to ask for at the practice, by reference.
        "vet_ask_for_address": {
          fieldKey: "vet_ask_for_address",
          name: "The practice: the person to ask for: address",
          fieldType: "address",
          placeholder: null,
          icon: "MapPin",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: null,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 46,
          columnWidth: null,
          layoutWidth: "half",
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        },
        "notes": {
          fieldKey: "notes",
          name: "Notes",
          fieldType: "notes",
          placeholder: null,
          icon: "FileText",
          options: null,
          isRequired: false,
          isCritical: false,
          wizardStage: 3,
          isEncrypted: false,
          isCategoryA: false,
          sortOrder: 7,
          columnWidth: 180,
          layoutWidth: null,
          fieldGroup: null,
          extractionKey: null,
          isActive: true,
          instructions: null,
          conditions: null,
          formTags: null,
          consumerTag: "both",
          ersRequired: false,
          wizardInclusionRule: "manual_only",
        }
        },
      }
  },
};
